import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Button,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import CustomSnackbar from "../../components/ui-component/snackbar/CustomSnackbar";
import { fDate } from "../../../utils/formatTime";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

function EmployeeViewForm({ data, onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [leaveRequest, setLeaveRequest] = useState(data);
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const [typeOfLeave, setTypeOfLeave] = useState(data?.leave_type_id?.id);
  const [typeOfLeaveDay, setTypeOfLeaveDay] = useState(data?.leave_day_type);
  const [leaveDateFrom, setLeaveDateFrom] = useState(data?.from_date);
  const [leaveTimeFrom, setLeaveTimeFrom] = useState(data?.from_time);
  const [leaveTimeTo, setLeaveTimeTo] = useState(data?.to_time);
  const [leaveDateTo, setLeaveDateTo] = useState(data?.to_date);
  const [numberOfDays, setNumberOfDays] = useState(0);
  const [additionalNote, setAdditionalNote] = useState(data?.additional_note);
  const [errors, setErrors] = useState({});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [expandDetails, setExpandDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleToggleExpand = () => {
    setExpandDetails(!expandDetails);
  };

  const handleTypeOfLeaveChange = (event) => {
    setTypeOfLeave(event.target.value);
  };

  const handleTypeOfLeaveDayChange = (event) => {
    setTypeOfLeaveDay(event.target.value);
  };

  const handleLeaveDateFromChange = (event) => {
    const date = event.target.value;
    setLeaveDateFrom(date);
    calculateNumberOfDays(date, leaveDateTo);
  };

  const handleLeaveDateToChange = (event) => {
    const date = event.target.value;
    setLeaveDateTo(date);
    calculateNumberOfDays(leaveDateFrom, date);
  };

  const handleAdditionalNoteChange = (event) => {
    setAdditionalNote(event.target.value);
  };

  const calculateNumberOfDays = (fromDate, toDate) => {
    if (fromDate && toDate) {
      let start = dayjs(fromDate);
      const end = dayjs(toDate);
      let days = 0;

      while (start <= end) {
        // If the day is not Sunday (0), count it
        if (start.day() !== 0) {
          days++;
        }
        start = start.add(1, "day");
      }

      setNumberOfDays(days);
    } else {
      setNumberOfDays(0);
    }
  };

  const handleFromTime = (e) => {
    setLeaveTimeFrom(e.target.value);
  };

  const handleToTime = (e) => {
    setLeaveTimeTo(e.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_ENDPOINT}empleavetype`);
        if (response.ok) {
          const data = await response.json();
          const sortedData = data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setLeaveTypes(sortedData);
        } else {
          throw new Error(`Request failed with status: ${response.status}`);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    calculateNumberOfDays(leaveDateFrom, leaveDateTo);
  }, []);

  const isEditable = leaveRequest?.leave_status === "pending";

  const handleUpdateStatus = (leaveId, newStatus) => {
    // Send a request to the server to update the status
    fetch(`${API_ENDPOINT}empleave/${leaveId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        leave_status: newStatus,
        user_id: parseInt(userId),
      }),
    })
      .then((response) => {
        if (response.ok) {
          showSnackbar("Leave status updated successfully", "success");
          setLeaveRequest((prev) => ({ ...prev, leave_status: newStatus }));
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          showSnackbar("Failed to update leave status", "error");
          throw new Error("Failed to update leave status");
        }
        // Handle successful response if needed
      })
      .catch((error) => {
        console.error("Error updating leave status:", error);
        showSnackbar("Error updating leave status", "error");
        // Revert the status locally if the server update fails
      });
  };

  const handleUpdateDetails = (leaveId) => {
    let leaveDetails = {
      leave_type_id: typeOfLeave,
      from_date: leaveDateFrom,
      to_date: leaveDateTo,
      additional_note: additionalNote,
      from_time: leaveTimeFrom,
      to_time: leaveTimeTo,
      user_id: parseInt(userId),
    };

    // Send a request to the server to update the status
    fetch(`${API_ENDPOINT}empleave/${leaveId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(leaveDetails),
    })
      .then((response) => {
        if (response.ok) {
          showSnackbar("Leave details updated successfully", "success");
          setLeaveRequest((prev) => ({
            ...prev,
            leave_type_id: { id: typeOfLeave },
            from_date: leaveDateFrom,
            to_date: leaveDateTo,
            additional_note: additionalNote,
          }));
        } else {
          showSnackbar("Failed to update leave details", "error");
          throw new Error("Failed to update leave status");
        }
        // Handle successful response if needed
      })
      .catch((error) => {
        console.error("Error updating leave status:", error);
        showSnackbar("Error updating leave details", "error");
        // Revert the status locally if the server update fails
      });
  };

  console.log("leaveRequest", leaveRequest);
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="h5">Employee Name</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            {leaveRequest?.emp_user_id?.first_name}{" "}
            {leaveRequest?.emp_user_id?.last_name}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5">Role</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{leaveRequest?.emp_user_id?.role}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5">Branch</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{leaveRequest?.emp_user_id?.branch_name}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5">Type Of Leave</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{leaveRequest?.leave_type_id?.leave_type}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5">Type Of Leave Day</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{leaveRequest?.leave_day_type}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5"> Leave Date From</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{fDate(leaveRequest?.from_date)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h5"> Leave Date To</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{fDate(leaveRequest?.to_date)}</Typography>
        </Grid>

        {leaveRequest?.leave_day_type !== "full" && (
          <>
            <Grid item xs={6}>
              <Typography variant="h5"> From Time</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{leaveRequest?.from_time}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="h5"> To Time</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{leaveRequest?.to_time}</Typography>
            </Grid>
          </>
        )}

        <Grid item xs={6}>
          <Typography variant="h5"> Requested Date</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{fDate(leaveRequest?.createdAt)}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h5"> Status</Typography>
        </Grid>
        <Grid item xs={6}>
          {leaveRequest?.leave_status === "accepted" && (
            <Chip
              size="normal"
              variant="contained"
              label="Approved"
              color="secondary"
            />
          )}
          {leaveRequest?.leave_status === "denied" && (
            <Chip
              size="normal"
              variant="contained"
              label="Denied"
              color="primary"
            />
          )}
          {leaveRequest?.leave_status === "pending" && (
            <Chip
              size="normal"
              variant="contained"
              label="Pending"
              color="warning"
            />
          )}
          {leaveRequest?.leave_status === "cancelled" && (
            <Chip
              size="normal"
              variant="contained"
              label="Cancelled"
              color="error"
            />
          )}
        </Grid>

        <Grid
          item
          xs={12}
          container
          alignItems="center"
          mt={1}
          sx={{ cursor: "pointer" }}
          onClick={handleToggleExpand}
        >
          <Typography variant="h4"> More Information</Typography>
          {!expandDetails ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </Grid>

        {expandDetails && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="h5"> Number of days requested</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography> {numberOfDays}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="h5">
                {leaveRequest?.leave_status === "accepted"
                  ? "Approved By"
                  : leaveRequest?.leave_status === "cancelled"
                  ? "Cancelled By"
                  : leaveRequest?.leave_status === "denied"
                  ? "Denied By"
                  : ""}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography>
                {leaveRequest?.approved_by?.first_name}{" "}
                {leaveRequest?.approved_by?.last_name}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="h5">
                {leaveRequest?.leave_status === "accepted"
                  ? "Approved Date"
                  : leaveRequest?.leave_status === "cancelled"
                  ? "Cancelled Date"
                  : leaveRequest?.leave_status === "denied"
                  ? "Denied Denied"
                  : ""}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography>{fDate(leaveRequest?.approved_date)}</Typography>
            </Grid>

            {leaveRequest.additional_note !== null && (
              <>
                <Grid item xs={6}>
                  <Typography variant="h5">
                    {" "}
                    Additional Note (Optional)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {" "}
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={leaveRequest?.additional_note}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "#b2b2b4", // Keep the border color gray on mouseover
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#b2b2b4", // Keep the border color gray even on focus
                          },
                        },
                      }}
                      InputProps={{
                        style: { borderRadius: "14px" }, // Add borderRadius here
                      }}
                    />
                  </Typography>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}

export default EmployeeViewForm;
